import { createTwoFilesPatch } from 'diff';
import type { ContextBlock, PipelineStage, SentFileRecord } from '../types';
import { draftText } from '../draft-text';
import { contentHash } from '../../nlp/content-hash';

export const c1SendDiffsNotFiles: PipelineStage = {
  id: 'C1',
  group: 'C',
  label: 'Send diffs, not files',
  description: 'If a previously-sent file changed, send a diff against the sent version instead of the full file.',
  impact: 'High',
  defaultEnabled: true,
  implemented: true,

  async run(draft, _params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));
    const sentFiles = draft.sentFiles ?? {};

    const sentFilesThisRound: Record<string, SentFileRecord> = { ...draft.sentFilesThisRound };
    let diffedCount = 0;
    let unchangedCount = 0;

    const newBlocks: ContextBlock[] = await Promise.all(
      draft.contextBlocks.map(async (block): Promise<ContextBlock> => {
        if (!block.id.startsWith('file:')) return block;
        const path = block.id.slice('file:'.length);
        const prior = sentFiles[path];
        const hash = await contentHash(block.content);

        if (!prior) {
          sentFilesThisRound[path] = { hash, content: block.content };
          return block;
        }

        if (prior.hash === hash) {
          unchangedCount++;
          sentFilesThisRound[path] = prior;
          return {
            ...block,
            label: `${path} (unchanged since last message)`,
            content: '(unchanged since last message — see earlier turn)',
            cacheable: false,
          };
        }

        diffedCount++;
        const patch = createTwoFilesPatch(path, path, prior.content, block.content, undefined, undefined, {
          context: 3,
        });
        sentFilesThisRound[path] = { hash, content: block.content };
        return { ...block, label: `${path} (changed — unified diff)`, content: patch, cacheable: false };
      }),
    );

    // Even when nothing was diffed/elided this round, sentFilesThisRound must still be threaded
    // through so the caller can persist first-seen file hashes — otherwise C1 never has anything
    // to diff against on the *next* message.
    const newDraft = { ...draft, contextBlocks: newBlocks, sentFilesThisRound };

    if (diffedCount === 0 && unchangedCount === 0) {
      return {
        draft: newDraft,
        report: {
          stageId: 'C1',
          tokensBefore,
          tokensAfter: tokensBefore,
          tokensSaved: 0,
          notes: ['No previously-sent file versions to diff against yet (first message referencing these files).'],
        },
      };
    }

    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'C1',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes: [
          `${diffedCount} file(s) sent as a diff against the last sent version, ${unchangedCount} file(s) unchanged and elided.`,
        ],
      },
    };
  },
};
